import { TSearchParams } from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { useState } from "react"
import { IUser } from "../Users"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
export type TTagScope = keyof typeof TAG_SCOPES

export type TType = "accounts" | "tourist-profile" | "trip-source" | TTagScope

export interface ITag {
	id: number
	name: string
	style?: {
		background_color?: string
		text_color?: string
	}
	pivot?: {
		created_at?: string
	}
}

export const TAG_SCOPES = {
	trips: {
		label: "Trips",
	},
} as const
export type TTagFilters = TSearchParams & {
	unused?: 1 | 0
}

export interface IOrgTag {
	id: number
	name: string
	description?: string
	style: {
		background_color: string
		text_color: string
	}
	usage_count: number
	created_at: string
	created_by: IUser
}

interface UseMergeEntitiesProps<T> {
	mergeUrl: string
	getId: (item: T) => number
	onSuccess: (primary: T) => void
}

export type TFiltersInLocationQuery = TSearchParams & {
	unused?: 1 | 0
}

export function filtersToQuery(filters: TTagFilters): TFiltersInLocationQuery {
	const { unused, q, page } = filters
	const query: TFiltersInLocationQuery = {}
	if (q) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (unused) {
		query.unused = 1
	}
	return query
}

export function queryToFilters(query: TFiltersInLocationQuery): TTagFilters {
	const { unused, q, page } = query
	const filters: TTagFilters = {}
	if (q) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (unused) {
		filters.unused = 1
	}
	return filters
}

export function filtersToAPIParams(params: TTagFilters) {
	return {
		...params,
		unused: params.unused === 1 ? 1 : undefined,
	}
}

export function useMergeEntities<T>({
	mergeUrl,
	getId,
	onSuccess,
}: UseMergeEntitiesProps<T>) {
	const xhr = useXHR()
	const [resetKey, setResetKey] = useState(0)

	const merge = async (
		primary: T,
		duplicates: T[],
		confirm: () => Promise<boolean>,
		entityLabel: string
	) => {
		try {
			if (!(await confirm())) return

			await xhr.post(mergeUrl, {
				primary: getId(primary),
				duplicates: duplicates.map(getId),
			})

			const mergeMore = window.confirm(
				`Do you want to merge more duplicate ${entityLabel}s?`
			)
			if (mergeMore) {
				setResetKey((k) => k + 1)
			} else {
				onSuccess(primary)
			}

			showSnackbar(`${entityLabel} merged successfully.`)
		} catch (error) {
			const e = error as Error
			alert(e.message || "Something went wrong")
		}
	}

	return { merge, resetKey }
}

export function isPrimaryRemoved<T>(
	primary: T | undefined,
	duplicates: T[],
	getId: (item: T) => string | number
) {
	if (!primary) return false

	return duplicates.every((item) => getId(item) !== getId(primary))
}
